import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, UserPlus, Menu, X } from 'lucide-react';
import { ROUTES } from 'utils/constants';
import { setLandingMenuOpen } from './landingMenuOpen';
import styles from './LandingNavbar.module.scss';

interface LandingNavbarProps {
  onLogin: () => void;
  onRegister: () => void;
  onScrollToServices: () => void;
  isLoading?: boolean;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}

const NAV_LINKS = [
  { label: 'Home', href: '#main-content' },
  { label: 'Services', href: '#hub-services' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

function LandingNavbar({
  onLogin,
  onRegister,
  isLoading,
  menuOpen,
  onMenuOpenChange,
}: LandingNavbarProps) {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setLandingMenuOpen(menuOpen);
    return () => setLandingMenuOpen(false);
  }, [menuOpen]);

  const handleAnchorClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    onMenuOpenChange(false);
    const target = document.querySelector(href);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`${styles.navbar} ${scrolled ? styles.scrolled : ''}`}
      role="banner"
      data-landing-navbar
    >
      <div className={styles.navInner}>
        <Link
          to={ROUTES.HOME}
          className={styles.brand}
          aria-label="Abrasoft Hub home"
          onClick={(e) => {
            if (location.pathname === ROUTES.HOME) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          <img
            src={`${import.meta.env.BASE_URL}favicon-32x32.png`}
            alt=""
            className={styles.brandLogo}
            aria-hidden="true"
            width={26}
            height={26}
          />
          <span className={styles.brandName}>Abrasoft Hub</span>
        </Link>

        <nav className={styles.navLinks} aria-label="Main navigation">
          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className={styles.navLink}
              onClick={(e) => handleAnchorClick(e, href)}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className={styles.navActions}>
          <button
            className={styles.btnGhost}
            onClick={onLogin}
            disabled={isLoading}
            type="button"
            aria-label="Sign in"
          >
            <LogIn size={16} aria-hidden="true" />
            <span>Sign In</span>
          </button>
          <button
            className={styles.btnPrimary}
            onClick={onRegister}
            disabled={isLoading}
            type="button"
            aria-label="Create account"
          >
            <UserPlus size={16} aria-hidden="true" />
            <span>Start free</span>
          </button>
        </div>

        <button
          className={styles.menuToggle}
          onClick={() => onMenuOpenChange(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          type="button"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {menuOpen && (
        <div
          className={styles.mobileMenu}
          role="dialog"
          aria-label="Mobile navigation"
        >
          <nav
            className={styles.mobileLinks}
            aria-label="Mobile navigation links"
          >
            {NAV_LINKS.map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className={styles.mobileLink}
                onClick={(e) => handleAnchorClick(e, href)}
              >
                {label}
              </a>
            ))}
          </nav>
          <div className={styles.mobileActions}>
            <button
              className={styles.mobileBtnGhost}
              onClick={() => {
                onMenuOpenChange(false);
                onLogin();
              }}
              disabled={isLoading}
              type="button"
            >
              <LogIn size={16} aria-hidden="true" />
              Sign In
            </button>
            <button
              className={styles.mobileBtnPrimary}
              onClick={() => {
                onMenuOpenChange(false);
                onRegister();
              }}
              disabled={isLoading}
              type="button"
            >
              <UserPlus size={16} aria-hidden="true" />
              Start free
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export default LandingNavbar;
